<template>
  <NewPasswordForm v-if="waitingForNewPassword" :loading="loading" @submit="onSubmitNewPassword" />
  <SetupMFAForm
    v-else-if="waitingForMFASetup"
    :loading="loading"
    :sharedSecret="mfaTotpSetupDetails.authenticatorCode"
    :qrCodeUrl="mfaTotpSetupDetails.qrCodeUrl"
    @submit="onSubmitSetupMFA"
  />

  <EnterMFAForm
    v-else-if="waitingForMFACode"
    :type="setMFACodeContinuationType"
    :loading="loading"
    @submit="onSubmitEnterMFA"
  />

  <LoginForm
    v-else-if="requiresCredentials"
    :enableForgotPassword="enableForgotPassword"
    :userNameType="userNameType"
    :loading="loading"
    @submit="onSubmit"
  />
  <div v-else class="flex flex-col items-start gap-4">
    <!-- Multiple Azure B2C policy buttons -->
    <template v-if="availablePolicies.length > 0">
      <InfuseButton
        v-for="policy in availablePolicies"
        :key="policy"
        size="large"
        :disabled="loading"
        @click="loginWithProvider(policy)"
      >
        <span class="inline-flex items-center gap-2 leading-none">
          <span>{{ t('auth.signIn.withAzureB2c', { policy: formatPolicyName(policy) }) }}</span>
          <img src="/favicon.ico" alt="" class="inline-block h-4 w-4 object-contain align-middle" />
        </span>
      </InfuseButton>
    </template>
    <!-- Fallback single button for Entra/other providers -->
    <InfuseButton v-else size="large" :disabled="loading" @click="loginWithProvider()">
      <span class="inline-flex items-center gap-2 leading-none">
        <span>{{ t('auth.signIn.withEntraId') }}</span>
        <img src="/favicon.ico" alt="" class="inline-block h-4 w-4 object-contain align-middle" />
      </span>
    </InfuseButton>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { EnterMFAForm, LoginForm, NewPasswordForm, SetupMFAForm } from '@infuse/auth/ui';
import { COGNITO_MFA_TYPE, LoginResult, useAuthenticator, AzureB2CAuthenticator } from '@infuse/auth/services';
import { useNotify } from '@infuse/ui/services';
import { UiNotificationType } from '@infuse/ui/domain';
import { InfuseButton } from '@infuse/ui/components';

const { t } = useI18n();
const notify = useNotify();

const loading = ref(false);
const authenticator = useAuthenticator();
const enableForgotPassword = authenticator.enableForgotPassword();
const userNameType = authenticator.userNameType();
const requiresCredentials = authenticator.requiresCredentials();

// Get available Azure B2C policies if using B2C authenticator
const availablePolicies = ref<string[]>([]);
if (authenticator instanceof AzureB2CAuthenticator) {
  availablePolicies.value = authenticator.getAvailablePolicies();
}

const setNewPasswordContinuation = ref();
const setMFACodeContinuation = ref();
const setMFACodeContinuationType = ref<COGNITO_MFA_TYPE>(COGNITO_MFA_TYPE.TOTP);

const setMFASetupContinuation = ref();
let mfaTotpSetupDetails = ref({ authenticatorCode: '', qrCodeUrl: '' });

const waitingForNewPassword = computed(() => setNewPasswordContinuation.value !== undefined);
const waitingForMFASetup = computed(() => setMFASetupContinuation.value !== undefined);
const waitingForMFACode = computed(() => setMFACodeContinuation.value !== undefined);

async function onSubmit(form: { username: string; password: string }) {
  loading.value = true;

  try {
    const result: LoginResult = await useAuthenticator().login(form.username, form.password);

    if (result.isLoggedIn) {
      notify({
        type: UiNotificationType.SUCCESS,
        group: 'notify-top',
        title: t('auth.notification.success_notification_title'),
        message: t('auth.notification.login_successful'),
        timeout: 5000
      });

      reload();
    } else if (result.setNewPassword) {
      setNewPasswordContinuation.value = result.setNewPassword;
    } else if (result.setupMFA && result.totpSetupDetails) {
      mfaTotpSetupDetails.value = result.totpSetupDetails;
      setMFASetupContinuation.value = result.setupMFA;
    } else if (result.setMFACode) {
      setMFACodeContinuation.value = result.setMFACode;
      setMFACodeContinuationType.value = COGNITO_MFA_TYPE.TOTP;
    } else if (result.setMailMFACode) {
      setMFACodeContinuation.value = result.setMailMFACode;
      setMFACodeContinuationType.value = COGNITO_MFA_TYPE.EMAIL;
    }
  } catch (e) {
    notify({
      type: UiNotificationType.ERROR,
      group: 'notify-top',
      title: t('auth.notification.error_notification_title'),
      message: t('auth.notification.invalid_credentials'),
      timeout: 5000
    });
  } finally {
    loading.value = false;
  }
}

async function onSubmitNewPassword(form: { password: string }) {
  if (!setNewPasswordContinuation.value) return;

  loading.value = true;

  try {
    await setNewPasswordContinuation.value(form.password);
    setNewPasswordContinuation.value = undefined;

    notify({
      type: UiNotificationType.SUCCESS,
      title: t('auth.notification.success_notification_title'),
      message: t('auth.notification.success_password_setup'),
      group: 'notify-top'
    });

    reload();
  } catch (error: unknown) {
    if (error instanceof Error) {
      if (error.toString().startsWith('InvalidPasswordException')) {
        notify({
          type: UiNotificationType.ERROR,
          title: t('auth.notification.invalid_password_notification_title'),
          message: error.toString().replace('InvalidPasswordException: ', ''),
          group: 'notify-top'
        });
      } else {
        notify({
          type: UiNotificationType.ERROR,
          title: t('auth.notification.error_notification_title'),
          message: t('auth.notification.error_notification_message'),
          group: 'notify-top'
        });
      }
    }
  } finally {
    loading.value = false;
  }
}
async function onSubmitSetupMFA(form: { otp: string }) {
  if (!setMFASetupContinuation.value) return;

  loading.value = true;

  try {
    await setMFASetupContinuation.value(form.otp);
    setMFASetupContinuation.value = undefined;

    notify({
      type: UiNotificationType.SUCCESS,
      group: 'notify-top',
      title: t('auth.notification.success_notification_title'),
      message: t('auth.notification.login_successful'),
      timeout: 5000
    });

    reload();
  } catch (e: unknown) {
    notify({
      type: UiNotificationType.ERROR,
      group: 'notify-top',
      title: t('auth.notification.error_notification_title'),
      message: t('auth.notification.invalid_mfa_token'),
      timeout: 5000
    });
  } finally {
    loading.value = false;
  }
}

async function onSubmitEnterMFA(form: { otp: string }) {
  if (!setMFACodeContinuation.value) return;

  loading.value = true;

  try {
    await setMFACodeContinuation.value(form.otp);
    setMFACodeContinuation.value = undefined;

    notify({
      type: UiNotificationType.SUCCESS,
      group: 'notify-top',
      title: t('auth.notification.success_notification_title'),
      message: t('auth.notification.login_successful'),
      timeout: 5000
    });

    reload();
  } catch (e: any) {
    notify({
      type: UiNotificationType.ERROR,
      group: 'notify-top',
      title: t('auth.notification.error_notification_title'),
      message: t('auth.notification.invalid_mfa_token'),
      timeout: 5000
    });
  } finally {
    loading.value = false;
  }
}

async function loginWithProvider(policy?: string) {
  loading.value = true;
  try {
    // Pass the policy as the first parameter (username position) for Azure B2C
    await authenticator.login(policy || '', '');
  } finally {
    loading.value = false;
  }
}

function formatPolicyName(policy: string): string {
  // Use the static helper to format policy names
  // e.g., "B2C_1_customers" -> "customers"
  return AzureB2CAuthenticator.getPolicyDisplayName(policy);
}

function reload() {
  // Reload the page after authentication so the configureApp function in main.ts gets reloaded and can perform an authenticated GET features call to the backend.
  window.location.reload();
}
</script>
